import { Reference } from '@apollo/client';

import Popover from '@components/popovers/Popover';
import PopoverItem from '@components/popovers/PopoverItem';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useOpener from '@hooks/useOpener';
import {
  GetJourneyWithPlanQuery,
  TaskFragment as Task,
  TaskDestroyDocument,
  TaskDestroyMutation,
  TaskDestroyMutationVariables,
  TaskToggleCompletedDocument,
  TaskToggleCompletedMutation,
  TaskToggleCompletedMutationVariables,
  TaskUpdateDocument,
  TaskUpdateMutation,
  TaskUpdateMutationVariables
} from '@typing/Generated';
import { getAllGoals } from '@utils/modelUtils';

type Props = {
  handleEdit: (task: Task) => void;
  journey?: GetJourneyWithPlanQuery['journey'];
  task: Task;
};

const TaskPopover = ({ handleEdit, journey, task }: Props) => {
  const popoverOpener = useOpener();

  const [taskArchive] = useMutationWithValidation<TaskUpdateMutation, TaskUpdateMutationVariables>(
    TaskUpdateDocument,
    'task',
    {
      update: store => {
        store.modify({
          fields: {
            archived() {
              return !task.archived;
            }
          },
          id: store.identify({ ...task })
        });
      }
    }
  );

  const updateArchived = () => {
    taskArchive({
      archived: !task.archived,
      id: task.id
    });
  };

  const [toggleTaskCompleted, apolloClient] = useMutationWithValidation<
    TaskToggleCompletedMutation,
    TaskToggleCompletedMutationVariables
  >(TaskToggleCompletedDocument, 'task', {
    update: () => {
      // This kicks out all of the journeyTasks queries so we will re-make them. If you're flipping between filters
      // then the queries related to them will be cached, but if completed changed then we need to redo those queries.
      apolloClient.cache.evict({ fieldName: 'journeyTasks', id: 'ROOT_QUERY' });
    }
  });

  const [destroyTask, client] = useMutationWithValidation<TaskDestroyMutation, TaskDestroyMutationVariables>(
    TaskDestroyDocument,
    'task',
    {
      update: (store, { data }) => {
        if (data?.taskDestroy?.id && journey) {
          store.modify({
            fields: {
              clientVisibleTasks(cachedRefs: readonly Reference[], { readField }) {
                return cachedRefs.filter(ref => data.taskDestroy?.id !== readField('id', ref));
              }
            },
            id: store.identify({ ...journey })
          });

          const goals = getAllGoals(journey).filter(goal => goal.tasks.some(t => t.id === data.taskDestroy?.id));

          goals.map(goal => {
            store.modify({
              fields: {
                tasks(cachedRefs: readonly Reference[], { readField }) {
                  return cachedRefs.filter(ref => data.taskDestroy?.id !== readField('id', ref));
                }
              },
              id: store.identify({ ...goal })
            });
          });
        } else {
          client.cache.evict({ fieldName: 'journeyTasks', id: 'ROOT_QUERY' });
          client.cache.evict({ fieldName: 'expertTasks', id: 'ROOT_QUERY' });
        }
      }
    }
  );

  const handleDestroy = () => {
    destroyTask({
      id: task.id
    });
  };

  const handleTaskToggleCompleted = () => {
    if (task) {
      toggleTaskCompleted({
        completed: !task.completed,
        id: task.id
      });
    }
  };

  return (
    <Popover isOpen={popoverOpener.isOpen} opener={popoverOpener}>
      <PopoverItem
        icon="edit"
        onClick={() => {
          popoverOpener.close();
          setTimeout(() => handleEdit(task), 150);
        }}
        textKey="dictionary.edit"
      />
      <PopoverItem
        icon="trash"
        onClick={() => {
          popoverOpener.close();
          handleDestroy();
        }}
        textKey="dictionary.delete"
      />
      {!task.clientVisible && (
        <PopoverItem
          icon={task.completed ? 'check' : 'circle'}
          iconColor="success-500"
          onClick={() => {
            popoverOpener.close();
            handleTaskToggleCompleted();
          }}
          textKey={
            task.completed
              ? 'pages.expert.tasks.popoverTitles.markAsIncomplete'
              : 'pages.expert.tasks.popoverTitles.markAsComplete'
          }
        />
      )}

      <PopoverItem
        icon="boxArchive"
        noBorder
        onClick={() => {
          popoverOpener.close();
          updateArchived();
        }}
        textKey={task.archived ? 'pages.carePlan.base.goals.unarchive' : 'pages.carePlan.base.goals.archive'}
      />
    </Popover>
  );
};

export default TaskPopover;
